import { useEffect } from 'react';
import { getDuSessionId, getDuBrowserId } from './duTrack';

type ExtraHeaders = Record<string, string>;
const PATCH_FLAG = '__globalFetchPatched__';

export function useGlobalFetchWithHeaders(extraHeaders?: ExtraHeaders) {
  useEffect(() => {
    // 只在浏览器端执行
    if (typeof window === 'undefined') return;

    const win = window as typeof window & { [PATCH_FLAG]?: boolean };

    // 避免重复 patch（包括多次调用 hook 的情况）
    if (win[PATCH_FLAG]) return;

    const originalFetch = window.fetch;
    const duConf = {
      du_api_key: process.env.NEXT_PUBLIC_DU_API_KEY,
      du_business_id: process.env.NEXT_PUBLIC_DU_BUSINESS_ID,
    };

    window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
      const headers = new Headers(init.headers || {});

      // 合并公共 headers（已有同名 header 时，不覆盖；如需覆盖改为 headers.set）
      if (extraHeaders) {
        Object.entries(extraHeaders).forEach(([key, value]) => {
          if (!headers.has(key)) {
            headers.set(key, value);
          }
        });
      }

      // 写入 DU 相关 header（如果 SDK / 配置不存在，就什么都不做）
      if ((win as any).du_web_sdk && duConf && duConf.du_api_key) {
        try {
          const url = typeof input === 'string' ? input : input instanceof URL ? input.href : '';

          const [duSessionId, duBrowserId] = await Promise.all([
            getDuSessionId({
              du_api_key: duConf.du_api_key,
              du_business_id: duConf.du_business_id,
              url,
            }),
            getDuBrowserId({
              du_api_key: duConf.du_api_key,
              du_business_id: duConf.du_business_id,
            }),
          ]);

          headers.set('du_session_id', duSessionId || '');
          headers.set('du_browser_id', duBrowserId || '');
        } catch {
          // 获取失败时忽略，不阻断请求
        }
      }

      return originalFetch(input, {
        ...init,
        headers,
      });
    };

    win[PATCH_FLAG] = true;
  }, [extraHeaders]); // extraHeaders 如果是动态对象，最好在外层用 useMemo 固定引用
}
